import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, X, ChevronDown } from "lucide-react";

/*
 * Searchable employee combobox — scales to thousands of employees.
 * Default mode searches the server (/hris/employees?search=) with debounce.
 * Pass `options` ([{ id, label }]) to filter a fixed list client-side instead
 * (e.g., only employees inside a payroll run).
 */
export default function EmployeePicker({ value, onChange, placeholder = "Search employee…", options = null, testId = "employee-picker" }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [label, setLabel] = useState("");
    const [loading, setLoading] = useState(false);
    const [hi, setHi] = useState(0);
    const boxRef = useRef(null);
    const debounce = useRef(null);

    const fmt = (e) => `${e.last_name}, ${e.first_name} (${e.employee_no})`;

    // Resolve a label for an externally-set value.
    useEffect(() => {
        if (!value) { setLabel(""); return; }
        if (options) {
            const m = options.find((o) => o.id === value);
            if (m) setLabel(m.label);
            return;
        }
        if (!label) {
            api.get(`/hris/employees/${value}`).then((r) => setLabel(fmt(r.data))).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, options]);

    // Close on outside click.
    useEffect(() => {
        const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    const clientMatches = useMemo(() => {
        if (!options) return null;
        const q = query.trim().toLowerCase();
        const all = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
        return all.slice(0, 50);
    }, [options, query]);

    const search = (q) => {
        if (options) return; // client mode
        clearTimeout(debounce.current);
        debounce.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/hris/employees", { params: { search: q || undefined, limit: 20 } });
                setResults((data.items || []).map((e) => ({ id: e.id, label: fmt(e), position: e.position })));
            } catch { setResults([]); }
            setLoading(false);
        }, 250);
    };

    const openList = () => { setOpen(true); setHi(0); if (!options) search(query); };
    const pick = (o) => { onChange(o.id); setLabel(o.label); setQuery(""); setOpen(false); };
    const clear = () => { onChange(""); setLabel(""); setQuery(""); };

    const list = options ? clientMatches : results;

    const onKey = (e) => {
        if (!open) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, (list?.length || 1) - 1)); }
        if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
        if (e.key === "Enter" && list?.[hi]) { e.preventDefault(); pick(list[hi]); }
        if (e.key === "Escape") setOpen(false);
    };

    return (
        <div className="relative" ref={boxRef} data-testid={testId}>
            {value && !open ? (
                <div className="w-full h-10 px-3 rounded-md border border-border text-sm flex items-center justify-between bg-background cursor-pointer" onClick={openList} data-testid={`${testId}-selected`}>
                    <span className="truncate">{label || "…"}</span>
                    <button type="button" className="text-muted-foreground hover:text-foreground shrink-0 ml-2" onClick={(e) => { e.stopPropagation(); clear(); }} title="Clear" data-testid={`${testId}-clear`}>
                        <X className="size-4" />
                    </button>
                </div>
            ) : (
                <div className="relative">
                    <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                        className="w-full h-10 pl-9 pr-8 rounded-md border border-border text-sm bg-background"
                        placeholder={placeholder}
                        value={query}
                        onFocus={openList}
                        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHi(0); search(e.target.value); }}
                        onKeyDown={onKey}
                        data-testid={`${testId}-input`}
                    />
                    <ChevronDown className="size-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
            )}
            {open && (
                <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border border-border bg-popover shadow-lg">
                    {loading && <div className="p-2 text-xs text-muted-foreground">Searching…</div>}
                    {!loading && !list?.length && <div className="p-2 text-xs text-muted-foreground">No employees match “{query}”.</div>}
                    {(list || []).map((o, i) => (
                        <button
                            key={o.id}
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm ${i === hi ? "bg-primary/10" : "hover:bg-secondary"}`}
                            onMouseEnter={() => setHi(i)}
                            onClick={() => pick(o)}
                            data-testid={`${testId}-opt-${o.id}`}
                        >
                            <div className="truncate">{o.label}</div>
                            {o.position && <div className="text-[10px] text-muted-foreground truncate">{o.position}</div>}
                        </button>
                    ))}
                    {!options && !loading && list?.length === 20 && <div className="p-1.5 text-[10px] text-center text-muted-foreground border-t border-border">Type to narrow down…</div>}
                </div>
            )}
        </div>
    );
}
